import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createProject } from '../services/projectService';
import { getTenants } from '../services/tenantService';
import { getMyProponentProfile } from '../services/proponentService';
import FootprintPickerMap from '../components/land/FootprintPickerMap';

const PROJECT_TYPES = [
  { value: 'public',    label: 'Public — visible to everyone' },
  { value: 'community', label: 'Community — visible to community members only' },
  { value: 'hybrid',    label: 'Hybrid — both public and community responses' },
];

const inp = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
  color: '#e2e8f0', padding: '8px 11px', fontSize: 13,
  width: '100%', boxSizing: 'border-box',
};
const label = { color: '#94a3b8', fontSize: 11, marginBottom: 4, display: 'block' };
const section = {
  background: '#1e293b', borderRadius: 10, padding: 22, marginBottom: 20,
};
const sectionTitle = { fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 16 };

// ─── ProjectFormPage ──────────────────────────────────────────────────────────
export default function ProjectFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Tenant list + proponent profile ──────────────────────────────────────
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    getTenants()
      .then(r => setTenants((r.data || []).filter(t => t.isActive)))
      .catch(console.error);

    if (user?.globalRole === 'proponent') {
      getMyProponentProfile()
        .then(r => {
          if (r.data?.orgName) {
            setForm(p => ({ ...p, proponentOrg: p.proponentOrg || r.data.orgName }));
          }
        })
        .catch(() => {}); // 404 = no profile yet, ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Section 1: Project Info ──────────────────────────────────────────────
  const [form, setForm] = useState({
    tenantId:     '',
    title:        '',
    projectNumber:'',
    proponentOrg: '',
    description:  '',
    type:         'public',
    startDate:    '',
    endDate:      '',
    address:      '',
    contactName:  '',
    contactPhone: '',
    contactEmail: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Section 2: Footprint ─────────────────────────────────────────────────
  const [footprintMode, setFootprintMode] = useState('draw'); // 'draw' | 'upload'
  const [vertices, setVertices]           = useState([]);     // [[lat,lng]...]
  const [uploadedGeometry, setUploadedGeometry] = useState(null);
  const [uploadError, setUploadError]     = useState('');
  const fileInputRef = useRef(null);

  // ── Submission state ─────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  // ── Footprint helpers ────────────────────────────────────────────────────
  const buildGeometry = () => {
    if (footprintMode === 'upload') return uploadedGeometry;
    if (vertices.length < 3) return null;
    // Close the ring
    const coords = [...vertices, vertices[0]].map(([lat, lng]) => [lng, lat]);
    return { type: 'Polygon', coordinates: [coords] };
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        // Accept either a GeoJSON Geometry or a FeatureCollection/Feature
        let geometry = null;
        if (json.type === 'FeatureCollection' && json.features?.length) {
          geometry = json.features[0].geometry;
        } else if (json.type === 'Feature') {
          geometry = json.geometry;
        } else if (['Polygon','MultiPolygon','Point','LineString','MultiLineString','MultiPoint'].includes(json.type)) {
          geometry = json;
        }
        if (!geometry) {
          setUploadError('Could not find a geometry in the file. Expected a GeoJSON Geometry, Feature, or FeatureCollection.');
          return;
        }
        setUploadedGeometry(geometry);
        setVertices([]);
      } catch {
        setUploadError('File is not valid JSON / GeoJSON.');
      }
    };
    reader.readAsText(file);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.tenantId) { setErr('Please select a community.'); return; }
    if (!form.title.trim()) { setErr('Project title is required.'); return; }

    const geometry = buildGeometry();

    setSaving(true);
    try {
      const payload = {
        tenantId:     form.tenantId,
        title:        form.title.trim(),
        description:  form.description.trim(),
        type:         form.type,
        projectNumber: form.projectNumber.trim() || undefined,
        proponentOrg: form.proponentOrg.trim() || undefined,
        address:      form.address.trim() || undefined,
        startDate:    form.startDate || undefined,
        endDate:      form.endDate || undefined,
        geometry:     geometry || undefined,
        contactName:  form.contactName.trim()  || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
      };
      const res = await createProject(payload);
      navigate(`/projects/${res.data._id}`);
    } catch (e2) {
      setErr(e2.response?.data?.message || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      padding: '28px 32px', maxWidth: 860, margin: '0 auto',
      color: '#e2e8f0', minHeight: '100vh', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 26 }}>
        <button
          type="button"
          onClick={() => navigate('/proponent')}
          style={{
            background: 'none', border: 'none', color: '#7dd3fc',
            cursor: 'pointer', fontSize: 20, padding: 0, lineHeight: 1,
          }}
          title="Back"
        >←</button>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>New Project</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Complete the project details and define your location footprint.
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── Section 1: Project Information ── */}
        <div style={section}>
          <div style={sectionTitle}>1 — Project Information</div>

          {/* Community */}
          <div style={{ marginBottom: 14 }}>
            <span style={label}>COMMUNITY *</span>
            <select style={inp} value={form.tenantId} onChange={e => set('tenantId', e.target.value)} required>
              <option value="">— Select a community —</option>
              {tenants.map(t => (
                <option key={t._id} value={t._id}>
                  {t.branding?.displayName || t.name}
                </option>
              ))}
            </select>
            {tenants.length === 0 && (
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>
                Loading communities…
              </div>
            )}
          </div>

          {/* Title + Project Number */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 240px' }}>
              <span style={label}>PROJECT TITLE *</span>
              <input style={inp} value={form.title}
                onChange={e => set('title', e.target.value)}
                placeholder="e.g. Highway 11 Expansion — Phase 2"
                required />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <span style={label}>PROJECT NUMBER</span>
              <input style={inp} value={form.projectNumber}
                onChange={e => set('projectNumber', e.target.value)}
                placeholder="e.g. ENV-2026-042" />
            </div>
          </div>

          {/* Org + Type */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 240px' }}>
              <span style={label}>PROPONENT ORGANIZATION</span>
              <input style={inp} value={form.proponentOrg}
                onChange={e => set('proponentOrg', e.target.value)}
                placeholder="e.g. Northlands Energy Corp." />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <span style={label}>VISIBILITY TYPE *</span>
              <select style={inp} value={form.type} onChange={e => set('type', e.target.value)}>
                {PROJECT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 14 }}>
            <span style={label}>DESCRIPTION</span>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 80 }}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Describe the project scope, objectives, and consultation requirements." />
          </div>

          {/* Civic address */}
          <div style={{ marginBottom: 14 }}>
            <span style={label}>LOCATION ADDRESS / DESCRIPTION</span>
            <input style={inp} value={form.address}
              onChange={e => set('address', e.target.value)}
              placeholder="e.g. NE¼ Sec 14 Twp 55 Rge 22 W4M, Alberta" />
          </div>

          {/* Dates */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: '1 1 150px' }}>
              <span style={label}>START DATE</span>
              <input style={inp} type="date" value={form.startDate}
                onChange={e => set('startDate', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <span style={label}>END DATE</span>
              <input style={inp} type="date" value={form.endDate}
                onChange={e => set('endDate', e.target.value)} />
            </div>
          </div>

          {/* Contact */}
          <div style={{
            borderTop: '1px solid #334155', paddingTop: 16, marginTop: 4,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 12 }}>
              PROJECT CONTACT (optional — overrides your profile default)
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ flex: '1 1 200px' }}>
                <span style={label}>CONTACT NAME</span>
                <input style={inp} value={form.contactName}
                  onChange={e => set('contactName', e.target.value)}
                  placeholder="e.g. Jane Smith" />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <span style={label}>CONTACT PHONE</span>
                <input style={inp} type="tel" value={form.contactPhone}
                  onChange={e => set('contactPhone', e.target.value)}
                  placeholder="+1 (403) 555-0123" />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <span style={label}>CONTACT EMAIL</span>
                <input style={inp} type="email" value={form.contactEmail}
                  onChange={e => set('contactEmail', e.target.value)}
                  placeholder="contact@example.com" />
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 2: Location Footprint ── */}
        <div style={section}>
          <div style={sectionTitle}>2 — Location Footprint</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
            Define your project's geographic footprint. This is used for land
            intelligence assessments and community visibility. All coordinates
            must be in WGS84 (EPSG:4326).
          </div>

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { key: 'draw',   label: '✏️ Draw on Map' },
              { key: 'upload', label: '📁 Upload GeoJSON' },
            ].map(({ key, label: lbl }) => (
              <button key={key} type="button"
                onClick={() => { setFootprintMode(key); setUploadError(''); }}
                style={{
                  background: footprintMode === key ? '#1e3a5f' : '#0f172a',
                  border: `1px solid ${footprintMode === key ? '#3b82f6' : '#334155'}`,
                  color: footprintMode === key ? '#7dd3fc' : '#94a3b8',
                  borderRadius: 6, padding: '6px 14px',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}>
                {lbl}
              </button>
            ))}

            {/* Current footprint status badge */}
            {(() => {
              const geo = buildGeometry();
              if (!geo) return null;
              return (
                <span style={{
                  background: '#14532d', border: '1px solid #22c55e',
                  borderRadius: 5, padding: '4px 10px',
                  fontSize: 11, color: '#86efac', marginLeft: 4,
                }}>
                  ✓ Footprint defined
                </span>
              );
            })()}
          </div>

          {/* Draw mode */}
          {footprintMode === 'draw' && (
            <FootprintPickerMap
              mode="draw"
              vertices={vertices}
              onVerticesChange={setVertices}
              height="380px"
            />
          )}

          {/* Upload mode */}
          {footprintMode === 'upload' && (
            <div>
              <div style={{
                background: '#0f172a', border: '1px dashed #334155',
                borderRadius: 8, padding: 16, marginBottom: 14, textAlign: 'center',
              }}>
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 10 }}>
                  Upload a GeoJSON file (.json or .geojson). Must be in <strong style={{ color: '#7dd3fc' }}>WGS84 (EPSG:4326)</strong>.
                  Accepts Geometry, Feature, or FeatureCollection (first feature used).
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.geojson,application/json,application/geo+json"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    background: '#1e3a5f', border: '1px solid #3b82f6',
                    borderRadius: 6, color: '#7dd3fc',
                    padding: '7px 16px', cursor: 'pointer', fontSize: 12,
                  }}>
                  Choose File
                </button>
                {uploadedGeometry && (
                  <span style={{ marginLeft: 12, fontSize: 12, color: '#86efac' }}>
                    ✓ {uploadedGeometry.type} loaded
                  </span>
                )}
              </div>
              {uploadError && (
                <div style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>{uploadError}</div>
              )}
              {uploadedGeometry && (
                <FootprintPickerMap
                  mode="view"
                  uploadedGeometry={uploadedGeometry}
                  readOnly
                  height="320px"
                />
              )}
            </div>
          )}
        </div>

        {/* ── Error + Submit ── */}
        {err && (
          <div style={{
            background: '#450a0a', border: '1px solid #ef4444',
            borderRadius: 8, padding: '10px 14px',
            color: '#f87171', fontSize: 13, marginBottom: 16,
          }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" disabled={saving} style={{
            background: '#1d4ed8', border: 'none', borderRadius: 7,
            color: '#fff', padding: '10px 24px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 14, fontWeight: 700,
            opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Saving…' : 'Create Project'}
          </button>
          <button type="button" onClick={() => navigate('/proponent')} style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: 7,
            color: '#94a3b8', padding: '10px 18px', cursor: 'pointer', fontSize: 14,
          }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
